import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, FiSearch, FiFilter, FiList, FiGrid, FiCheck, FiTrash2, 
  FiEye, FiTrendingUp, FiTarget, FiDollarSign, FiUser, FiChevronLeft, FiChevronRight, FiRefreshCw 
} from 'react-icons/fi';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';
import StatCard from '../../components/shared/StatCard';
import { leadAPI, clientAPI, propertyAPI } from '../../services/api';

const STATUS_CONFIG = {
  new:         { label: 'Nouveau',      color: '#3b82f6', bg: '#dbeafe' },
  contacted:   { label: 'Contacté',     color: '#f59e0b', bg: '#fef3c7' },
  qualified:   { label: 'Qualifié',     color: '#8b5cf6', bg: '#ede9fe' },
  proposal:    { label: 'Proposition',  color: '#06b6d4', bg: '#cffaff' },
  negotiation: { label: 'Négociation',  color: '#f97316', bg: '#ffedd5' },
  won:         { label: 'Gagné (Client)', color: '#22c55e', bg: '#dcfce7' },
  lost:        { label: 'Perdu',        color: '#ef4444', bg: '#fee2e2' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Basse',   cls: 'badge-secondary' },
  medium: { label: 'Moyenne', cls: 'badge-info' },
  high:   { label: 'Haute',   cls: 'badge-warning' },
  urgent: { label: 'Urgente', cls: 'badge-danger' },
};

const SOURCES_LIST = [
  'Site Web',
  'WhatsApp',
  'Bouche-à-oreille',
  'Réseaux Sociaux',
  'Portail Immobilier',
  'Panneau / Affiche',
  'Recommandation',
  'Autre',
];

const LeadListPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pipeline'); // default pipeline kanban
  const [showModal, setShowModal] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const [clientMode, setClientMode] = useState('existing'); // 'existing' ou 'new'
  const [form, setForm] = useState({
    client_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    client_category: 'buyer',
    property_id: '',
    status: 'new',
    priority: 'medium',
    source: 'Site Web',
    budget_min: '',
    budget_max: '',
    desired_type: '',
    desired_location: '',
    score: 30,
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, pipeRes] = await Promise.all([
        leadAPI.list({
          search: search || undefined,
          status: statusFilter || undefined,
          priority: priorityFilter || undefined,
          source: sourceFilter || undefined,
        }),
        leadAPI.getPipeline(),
      ]);
      setLeads(leadsRes.data || []);
      setPipeline(pipeRes.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [cliRes, propRes] = await Promise.all([
        clientAPI.list({ limit: 100 }),
        propertyAPI.list({ limit: 100 }),
      ]);
      const cliList = Array.isArray(cliRes.data) ? cliRes.data : (cliRes.data?.clients || cliRes.data?.data || []);
      const propList = Array.isArray(propRes.data) ? propRes.data : (propRes.data?.properties || propRes.data?.data || []);
      setClients(cliList);
      setProperties(propList);
    } catch (e) {
      console.error('Erreur chargement clients/propriétés:', e);
      setClients([]);
      setProperties([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [search, statusFilter, priorityFilter, sourceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        client_id: clientMode === 'existing' ? (form.client_id || null) : null,
        first_name: clientMode === 'new' ? form.first_name : undefined,
        last_name: clientMode === 'new' ? form.last_name : undefined,
        email: clientMode === 'new' ? form.email : undefined,
        phone: clientMode === 'new' ? form.phone : undefined,
        client_category: clientMode === 'new' ? form.client_category : undefined,
        property_id: form.property_id || null,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        score: parseInt(form.score || 30),
      };
      await leadAPI.create(payload);
      setShowModal(false);
      setForm({
        client_id: '', first_name: '', last_name: '', email: '', phone: '', client_category: 'buyer',
        property_id: '', status: 'new', priority: 'medium', source: 'Site Web',
        budget_min: '', budget_max: '', desired_type: '', desired_location: '', score: 30, notes: '',
      });
      fetchData();
      fetchDropdownData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la création du lead');
    }
  };

  const handleConvert = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Convertir ce lead en client actif ?')) return;
    try {
      await leadAPI.convert(id);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la conversion');
    }
  };

  const handleStatusShift = async (e, leadId, currentStatus, direction) => {
    e.stopPropagation();
    const stages = Object.keys(STATUS_CONFIG);
    const currIdx = stages.indexOf(currentStatus);
    const newIdx = direction === 'next' ? currIdx + 1 : currIdx - 1;
    if (newIdx < 0 || newIdx >= stages.length) return;

    const newStatus = stages[newIdx];
    try {
      await leadAPI.update(leadId, { status: newStatus });
      fetchData();
    } catch (e) {
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce lead ?')) return;
    try {
      await leadAPI.delete(id);
      fetchData();
    } catch (e) {
      alert('Erreur lors de la suppression');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

  // KPIs
  const totalLeads = leads.length;
  const totalPipelineBudget = leads.reduce((acc, l) => acc + parseFloat(l.budget_max || 0), 0);
  const qualifiedCount = leads.filter(l => ['qualified', 'proposal', 'negotiation'].includes(l.status)).length;
  const wonCount = leads.filter(l => l.status === 'won').length;
  const conversionRate = totalLeads > 0 ? Math.round((wonCount / totalLeads) * 100) : 0;

  // Colonnes DataTable
  const columns = [
    {
      header: 'Client / Contact',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {r.client_first_name || r.client_last_name 
              ? `${r.client_first_name || ''} ${r.client_last_name || ''}`.trim()
              : 'Prospect Anonyme'}
          </div>
          {r.client_email && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.client_email}</div>}
        </div>
      ),
    },
    {
      header: 'Bien / Recherche',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.property_title || r.desired_type || 'Général'}</div>
          {r.desired_location && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>📍 {r.desired_location}</div>}
        </div>
      ),
    },
    {
      header: 'Budget',
      render: (r) => (
        <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
          {r.budget_max ? `${fmt(r.budget_max)} XOF` : r.budget_min ? `>= ${fmt(r.budget_min)} XOF` : 'Non défini'}
        </span>
      ),
    },
    {
      header: 'Priorité',
      render: (r) => {
        const cfg = PRIORITY_CONFIG[r.priority] || PRIORITY_CONFIG.medium;
        return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
      },
    },
    {
      header: 'Source',
      render: (r) => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{r.source || '—'}</span>,
    },
    {
      header: 'Statut',
      render: (r) => {
        const cfg = STATUS_CONFIG[r.status] || { label: r.status, color: '#64748b', bg: '#f1f5f9' };
        return (
          <span className="badge" style={{ background: cfg.bg, color: cfg.color, fontWeight: 600 }}>
            {cfg.label}
          </span>
        );
      },
    },
    {
      header: 'Score',
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, background: '#e2e8f0', height: 6, borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
            <div style={{
              width: `${Math.min(100, r.score || 0)}%`,
              height: '100%',
              background: (r.score >= 70 ? '#22c55e' : r.score >= 40 ? '#f59e0b' : '#3b82f6'),
            }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{r.score || 0}%</span>
        </div>
      ),
    },
    {
      header: 'Agent',
      render: (r) => (
        <span style={{ fontSize: 12 }}>
          {r.assigned_first_name ? `${r.assigned_first_name} ${r.assigned_last_name || ''}` : 'Non assigné'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          {!['won', 'lost'].includes(r.status) && (
            <button
              className="btn btn-sm btn-success"
              onClick={(e) => handleConvert(e, r.id)}
              title="Convertir en client"
            >
              <FiCheck />
            </button>
          )}
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => navigate(`/leads/${r.id}`)}
            title="Détails"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={(e) => handleDelete(e, r.id)}
            title="Supprimer"
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header Page */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Gestion des Leads & Prospection</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Suivez et qualifiez vos opportunités commerciales du premier contact jusqu'à la conversion
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn btn-sm ${view === 'pipeline' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('pipeline')}
          >
            <FiGrid style={{ marginRight: 4 }} /> Pipeline (Kanban)
          </button>
          <button
            className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('list')}
          >
            <FiList style={{ marginRight: 4 }} /> Vue Liste
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
            <FiPlus style={{ marginRight: 4 }} /> Nouveau Lead
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          icon={<FiUser />}
          label="Total Pistes / Leads"
          value={totalLeads}
          color="#3b82f6"
          bgColor="#dbeafe"
        />
        <StatCard
          icon={<FiDollarSign />}
          label="Valeur du Pipeline"
          value={`${fmt(totalPipelineBudget)} XOF`}
          color="#7c3aed"
          bgColor="#ede9fe"
        />
        <StatCard
          icon={<FiTarget />}
          label="Leads Qualifiés / En Négociation"
          value={qualifiedCount}
          color="#f59e0b"
          bgColor="#fef3c7"
        />
        <StatCard
          icon={<FiTrendingUp />}
          label="Taux de Conversion"
          value={`${conversionRate}%`}
          color="#22c55e"
          bgColor="#dcfce7"
        />
      </div>

      {/* Barre de filtres */}
      <div className="card" style={{ marginBottom: 24, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: 36 }}
              placeholder="Rechercher par nom client, bien, lieu, source..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ width: 160 }}>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          <div style={{ width: 150 }}>
            <select
              className="form-control"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">Toutes priorités</option>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          <div style={{ width: 160 }}>
            <select
              className="form-control"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">Toutes les sources</option>
              {SOURCES_LIST.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {(search || statusFilter || priorityFilter || sourceFilter) && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setSearch(''); setStatusFilter(''); setPriorityFilter(''); setSourceFilter(''); }}
            >
              <FiRefreshCw style={{ marginRight: 4 }} /> Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Chargement */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : view === 'list' ? (
        /* VUE LISTE DATATABLE */
        <div className="card">
          <DataTable
            columns={columns}
            data={leads}
            onRowClick={(r) => navigate(`/leads/${r.id}`)}
            emptyMessage="Aucun lead trouvé"
          />
        </div>
      ) : (
        /* VUE PIPELINE (KANBAN) */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
          alignItems: 'start',
          overflowX: 'auto',
          paddingBottom: 16
        }}>
          {Object.entries(STATUS_CONFIG).map(([stageKey, stageCfg]) => {
            const stageLeads = leads.filter((l) => l.status === stageKey);
            const stageBudgetSum = stageLeads.reduce((acc, l) => acc + parseFloat(l.budget_max || 0), 0);

            return (
              <div
                key={stageKey}
                style={{
                  background: 'var(--bg-secondary || #f8fafc)',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '80vh',
                }}
              >
                {/* Header de colonne */}
                <div style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--border)',
                  borderTop: `4px solid ${stageCfg.color}`,
                  borderRadius: '10px 10px 0 0',
                  background: '#fff',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: stageCfg.color }}>
                      {stageCfg.label}
                    </span>
                    <span style={{
                      background: stageCfg.bg,
                      color: stageCfg.color,
                      fontWeight: 700,
                      fontSize: 12,
                      padding: '2px 8px',
                      borderRadius: 12,
                    }}>
                      {stageLeads.length}
                    </span>
                  </div>
                  {stageBudgetSum > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                      Vol: <strong>{fmt(stageBudgetSum)} XOF</strong>
                    </div>
                  )}
                </div>

                {/* Cartes de leads dans cette étape */}
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
                  {stageLeads.map((l) => {
                    const prio = PRIORITY_CONFIG[l.priority] || PRIORITY_CONFIG.medium;
                    return (
                      <div
                        key={l.id}
                        onClick={() => navigate(`/leads/${l.id}`)}
                        className="card"
                        style={{
                          margin: 0,
                          padding: 12,
                          cursor: 'pointer',
                          borderRadius: 8,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transition: 'all 0.15s ease',
                          borderLeft: `4px solid ${stageCfg.color}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>
                            {l.client_first_name || l.client_last_name 
                              ? `${l.client_first_name || ''} ${l.client_last_name || ''}`.trim()
                              : 'Lead Anonyme'}
                          </span>
                          <span className={`badge ${prio.cls}`} style={{ fontSize: 10 }}>
                            {prio.label}
                          </span>
                        </div>

                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                          🏢 {l.property_title || l.desired_type || 'Recherche générale'}
                          {l.desired_location ? ` — ${l.desired_location}` : ''}
                        </div>

                        {l.budget_max && (
                          <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 13, marginBottom: 8 }}>
                            {fmt(l.budget_max)} XOF
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justify: 'space-between',
                          paddingTop: 6,
                          borderTop: '1px solid #f1f5f9',
                          fontSize: 11
                        }}>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            Score: <strong>{l.score || 0}%</strong>
                          </span>

                          <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                            {stageKey !== 'new' && (
                              <button
                                className="btn btn-sm btn-secondary"
                                style={{ padding: '2px 6px', fontSize: 10 }}
                                onClick={(e) => handleStatusShift(e, l.id, l.status, 'prev')}
                                title="Étape précédente"
                              >
                                <FiChevronLeft />
                              </button>
                            )}
                            {stageKey !== 'lost' && stageKey !== 'won' && (
                              <button
                                className="btn btn-sm btn-secondary"
                                style={{ padding: '2px 6px', fontSize: 10 }}
                                onClick={(e) => handleStatusShift(e, l.id, l.status, 'next')}
                                title="Étape suivante"
                              >
                                <FiChevronRight />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {stageLeads.length === 0 && (
                    <div style={{
                      padding: 20,
                      textAlign: 'center',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      border: '1px dashed var(--border)',
                      borderRadius: 8,
                    }}>
                      Aucun lead
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Création Nouveau Lead */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Créer une nouvelle piste (Lead)">
        <form onSubmit={handleCreate}>
          {/* Choix du mode Client: Existant vs Nouveau */}
          <div style={{
            display: 'flex', gap: 8, marginBottom: 16, background: '#f1f5f9', padding: 4, borderRadius: 8
          }}>
            <button
              type="button"
              className={`btn btn-sm ${clientMode === 'existing' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={() => setClientMode('existing')}
            >
              👤 Client CRM existant
            </button>
            <button
              type="button"
              className={`btn btn-sm ${clientMode === 'new' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1 }}
              onClick={() => setClientMode('new')}
            >
              ➕ Nouveau prospect (Nouveau contact)
            </button>
          </div>

          {clientMode === 'existing' ? (
            <div className="grid-2">
              <div className="form-group">
                <label>Sélectionner le Client / Prospect</label>
                <select
                  className="form-control"
                  value={form.client_id}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                >
                  <option value="">-- Aucun client présélectionné --</option>
                  {(Array.isArray(clients) ? clients : []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} ({c.email || c.phone || 'Prospect'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Bien immobilier d'intérêt (optionnel)</label>
                <select
                  className="form-control"
                  value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                >
                  <option value="">-- Sélectionner un bien --</option>
                  {(Array.isArray(properties) ? properties : []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.city || ''} - {p.price ? `${fmt(p.price)} XOF` : ''})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="grid-2">
                <div className="form-group">
                  <label>Prénom du prospect *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ex: Ibrahim"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required={clientMode === 'new'}
                  />
                </div>
                <div className="form-group">
                  <label>Nom du prospect *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ex: Traoré"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required={clientMode === 'new'}
                  />
                </div>
              </div>

              <div className="grid-3">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="ibrahim@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="+223 70 00 00 00"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Rôle / Profil du Prospect</label>
                  <select
                    className="form-control"
                    value={form.client_category}
                    onChange={(e) => setForm({ ...form, client_category: e.target.value })}
                  >
                    <option value="tenant">Candidat Locataire (Cherche location)</option>
                    <option value="buyer">Candidat Acheteur (Cherche achat)</option>
                    <option value="owner">Propriétaire Mandant (Veut gérer/vendre)</option>
                    <option value="prospect">Prospect Général</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Bien immobilier d'intérêt (optionnel)</label>
                <select
                  className="form-control"
                  value={form.property_id}
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                >
                  <option value="">-- Sélectionner un bien --</option>
                  {(Array.isArray(properties) ? properties : []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.city || ''} - {p.price ? `${fmt(p.price)} XOF` : ''})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="grid-2">
            <div className="form-group">
              <label>Statut initial</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priorité</label>
              <select
                className="form-control"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Source de la piste</label>
              <select
                className="form-control"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              >
                {SOURCES_LIST.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Score initial (0 à 100)</label>
              <input
                type="number"
                min="0"
                max="100"
                className="form-control"
                value={form.score}
                onChange={(e) => setForm({ ...form, score: e.target.value })}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Budget Minimum (XOF)</label>
              <input
                type="number"
                className="form-control"
                placeholder="Ex: 5000000"
                value={form.budget_min}
                onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Budget Maximum (XOF)</label>
              <input
                type="number"
                className="form-control"
                placeholder="Ex: 25000000"
                value={form.budget_max}
                onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Type de bien recherché</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: Villa F5, Appartement 3 pièces..."
                value={form.desired_type}
                onChange={(e) => setForm({ ...form, desired_type: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Localisation / Quartier recherché</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: Bamako Coura, ACI 2000, Hamdallaye..."
                value={form.desired_location}
                onChange={(e) => setForm({ ...form, desired_location: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes & Détails du besoin</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Spécifications, préférences, délais..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary">
              Créer la piste
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LeadListPage;
